import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { reviewService } from '../../../services/reviewService';

const StoreReviewsTab = ({ sellerId }) => {
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [respondingTo, setRespondingTo] = useState(null);
    const [responseText, setResponseText] = useState('');
    const [submittingResponse, setSubmittingResponse] = useState(false);

    useEffect(() => {
        const fetchReviews = async () => {
            if (!sellerId) {
                setError('Seller ID not provided.');
                setLoading(false);
                return;
            }
            try {
                setLoading(true);
                const data = await reviewService.getReviewsForSeller(sellerId);
                setReviews(data);
                setError('');
            } catch (err) {
                console.error('Error fetching store reviews:', err);
                setError(err.message || 'Failed to fetch store reviews.');
            } finally {
                setLoading(false);
            }
        };

        fetchReviews();
    }, [sellerId]);

    const handleRespond = async (reviewId, reviewType) => {
        if (!responseText.trim()) return;
        try {
            setSubmittingResponse(true);
            const updatedReview = await reviewService.respondToReview(reviewId, reviewType, responseText);
            setReviews(reviews.map(r => r.id === reviewId ? { ...r, sellerResponse: updatedReview.sellerResponse, sellerResponseAt: updatedReview.sellerResponseAt } : r));
            setRespondingTo(null);
            setResponseText('');
        } catch (err) {
            console.error('Error responding to review:', err);
            alert('Failed to submit response.');
        } finally {
            setSubmittingResponse(false);
        }
    };

    const totalReviews = reviews.length;
    const averageRating = totalReviews > 0 ? (reviews.reduce((acc, review) => acc + review.rating, 0) / totalReviews).toFixed(1) : 0;

    const starDistribution = [0, 0, 0, 0, 0]; // Index 0 for 1-star, up to 4 for 5-star
    reviews.forEach(review => {
        if (review.rating >= 1 && review.rating <= 5) {
            starDistribution[review.rating - 1]++;
        }
    });

    if (loading) {
        return <div>Loading reviews...</div>;
    }

    if (error) {
        return <div>Error: {error}</div>;
    }

    return (
        <div className="space-y-8">
            <h2 className="text-2xl font-bold text-foreground mb-6">Store Reviews</h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-card p-4 rounded-lg border">
                    <h3 className="text-md font-medium text-foreground">Total Reviews</h3>
                    <p className="text-2xl font-bold text-primary">{totalReviews}</p>
                </div>
                <div className="bg-card p-4 rounded-lg border">
                    <h3 className="text-md font-medium text-foreground mb-3">Star Distribution</h3>
                    <div className="space-y-1">
                        {[5, 4, 3, 2, 1].map(star => (
                            <div key={star} className="flex items-center">
                                <span className="text-sm font-medium text-foreground w-6">{star}</span>
                                <div className="w-full bg-gray-200 rounded-full h-2 mx-2">
                                    <div
                                        className="bg-primary h-2 rounded-full"
                                        style={{ width: `${(starDistribution[star - 1] / totalReviews) * 100 || 0}%` }}
                                    ></div>
                                </div>
                                <span className="text-sm text-muted-foreground">{starDistribution[star - 1]}</span>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="bg-card p-4 rounded-lg border">
                    <h3 className="text-md font-medium text-foreground">Average Rating</h3>
                    <p className="text-2xl font-bold text-primary">{averageRating} / 5</p>
                </div>
            </div>

            <div className="space-y-4">
                {reviews.map(review => (
                    <div key={review.id} className="bg-card p-4 rounded-lg border">
                        <div className="flex items-center mb-2">
                            <div className="flex items-center">
                                {[...Array(5)].map((_, i) => (
                                    <svg key={i} className={`w-5 h-5 ${i < review.rating ? 'text-yellow-400' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                                    </svg>
                                ))}
                            </div>
                            <h3 className="font-bold ml-3 text-foreground">{review.reviewedItemName}</h3>
                            <span className="ml-3 text-xs font-semibold inline-flex items-center px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-800">
                                {review.reviewType}
                            </span>
                        </div>
                        <p className="text-muted-foreground mt-2">{review.review}</p>
                        <p className="text-sm text-muted-foreground mt-2">By {review.reviewer?.firstName ? `${review.reviewer.firstName} ${review.reviewer.lastName}` : 'Anonymous User'} on {new Date(review.createdAt).toLocaleDateString()}</p>

                        {/* Seller Response Section */}
                        {review.sellerResponse ? (
                            <div className="mt-4 bg-gray-50 p-4 rounded-md border border-gray-100">
                                <p className="text-sm font-semibold text-gray-900 mb-1">Your Response</p>
                                <p className="text-sm text-gray-700">{review.sellerResponse}</p>
                                {review.sellerResponseAt && <p className="text-xs text-gray-500 mt-1">Responded on {new Date(review.sellerResponseAt).toLocaleDateString()}</p>}
                            </div>
                        ) : (
                            <div className="mt-4 border-t pt-4">
                                {respondingTo === review.id ? (
                                    <div className="space-y-3 flex flex-col items-end">
                                        <textarea
                                            className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-primary focus:border-primary"
                                            rows="3"
                                            placeholder="Write a public response to this review..."
                                            value={responseText}
                                            onChange={(e) => setResponseText(e.target.value)}
                                        ></textarea>
                                        <div className="flex space-x-2">
                                            <button 
                                                className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                                                onClick={() => { setRespondingTo(null); setResponseText(''); }}
                                                disabled={submittingResponse}
                                            >
                                                Cancel
                                            </button>
                                            <button 
                                                className="px-3 py-1.5 text-sm text-white bg-primary rounded hover:bg-primary/90"
                                                onClick={() => handleRespond(review.id, review.reviewType)}
                                                disabled={submittingResponse || !responseText.trim()}
                                            >
                                                {submittingResponse ? 'Submitting...' : 'Post Response'}
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <button 
                                        className="text-sm text-primary hover:text-primary/80 font-medium"
                                        onClick={() => setRespondingTo(review.id)}
                                    >
                                        Respond to this review
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default StoreReviewsTab;